import { useCallback, useEffect, useRef, useState } from "react";
import { clearSession, getConciergeStatus, getServicesKnowledge, streamMessage } from "../../lib/concierge";
import { newId } from "../../lib/api";

const URGENT = /\b(emergency|fire|smoke|break[- ]?in|burglar\w*|robber\w*|intruder\w*|armed|attack\w*|injur\w*|bleeding|ambulance|police|gun|kidnap\w*)\b/i;
const SAFETY = "If anyone is in danger or a crime is in progress, contact emergency services now on 999 or 112. TechCare does not provide live emergency dispatch or monitoring.";

const STEPS = { quote: ["services", "need"], site_survey: ["services", "location", "need"], contact: ["need"] };
const ASK = {
  services: "Which services is this about? Pick any that apply, or skip if you're not sure.",
  location: "Where is the site? A town or area is enough, for example Westlands, Nairobi.",
  need: {
    quote: "Briefly describe what you'd like a quote for: equipment or work involved, number of sites or users, and timing.",
    site_survey: "Tell us about the site and what you'd like assessed: approximate size, floors or areas, and timing.",
    contact: "How can our team help? Type your message below.",
  },
};
const INTRO = { quote: "Let's prepare a quote request.", site_survey: "Let's prepare a site-survey request.", contact: "Let's get a message to our team." };

let uid = 0;
const id = () => `m${++uid}`;

export function useConcierge() {
  const [status, setStatus] = useState({ ai_enabled: false, mode: "guided" });
  const [messages, setMessages] = useState([]);
  const [flow, setFlow] = useState(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(Date.now());

  useEffect(() => {
    let alive = true;
    getConciergeStatus().then((s) => { if (alive) setStatus(s); });
    return () => { alive = false; };
  }, []);

  const push = useCallback((...ms) => setMessages((xs) => [...xs, ...ms.map((m) => ({ id: id(), ...m }))]), []);
  const guide = (text) => ({ role: "assistant", source: "guide", text });

  const ask = useCallback((type, step) => {
    if (step === "services") push(guide(ASK.services), { kind: "picker", type });
    else push(guide(step === "need" ? ASK.need[type] : ASK[step]));
  }, [push]);

  const startFlow = useCallback((type, preset = {}) => {
    started.current = Date.now();
    const steps = STEPS[type].filter((s) => !(s === "services" && preset.services?.length));
    setFlow({ type, steps, i: 0, data: { services: [], ...preset } });
    push(guide(INTRO[type]));
    ask(type, steps[0]);
  }, [ask, push]);

  const advance = useCallback((f, patch) => {
    const data = { ...f.data, ...patch };
    const i = f.i + 1;
    if (i < f.steps.length) {
      setFlow({ ...f, i, data });
      ask(f.type, f.steps[i]);
      return;
    }
    setFlow(null);
    push(guide("Thank you. Here's a summary. Add your contact details, check everything and press Confirm."),
      { kind: "review", draft: { type: f.type, services: data.services, location: data.location ?? "", message: data.message ?? "" }, startedAt: started.current });
  }, [ask, push]);

  const pickServices = useCallback((picked) => {
    if (!flow) return;
    setMessages((xs) => xs.filter((m) => m.kind !== "picker"));
    push({ role: "user", text: picked.length ? picked.join(", ") : "Not sure yet" });
    advance(flow, { services: picked });
  }, [advance, flow, push]);

  const action = useCallback((key) => {
    setFlow(null);
    setMessages((xs) => xs.filter((m) => m.kind !== "picker"));
    if (key === "services") {
      push(guide("Here are TechCare's services. Open one to learn more, or request a quote."));
      getServicesKnowledge().then((items) => push({ kind: "services", items }))
        .catch(() => push(guide("I couldn't load our services just now. You can browse them on the Services page.")));
    } else if (key === "track") push(guide("You can check your request securely on our tracking page."), { kind: "track" });
    else if (key === "contact") push(guide("Prefer to speak with a person? Here's how to reach us."), { kind: "contact" });
    else startFlow(key);
  }, [push, startFlow]);

  const sendAi = useCallback(async (text, cid = newId(), existing = null) => {
    const reply = existing ?? id();
    const fresh = { id: reply, role: "assistant", source: "ai", text: "", pending: true, startedAt: Date.now() };
    setMessages((xs) => (existing ? xs.map((m) => (m.id === reply ? fresh : m)) : [...xs, fresh]));
    setBusy(true);
    const patch = (fn) => setMessages((xs) => xs.map((m) => (m.id === reply ? fn(m) : m)));
    await streamMessage(text, cid, (ev) => {
      if (ev.type === "delta") patch((m) => ({ ...m, text: m.text + ev.text }));
      else if (ev.type === "safety") push({ kind: "safety", text: ev.text });
      else if (ev.type === "privacy") push({ kind: "notice", text: ev.text });
      else if (ev.type === "draft") push({ kind: "review", draft: ev.draft, startedAt: Date.now() - 5000 });
      else if (ev.type === "error") {
        patch((m) => ({ ...m, source: "guide", text: ev.text, failed: true, retry: ev.retry ? { text, cid } : null }));
        if (ev.code === "limit") getConciergeStatus().then(setStatus);
      }
    }, status.total_timeout_s);
    patch((m) => ({ ...m, pending: false }));
    setBusy(false);
  }, [push, status.total_timeout_s]);

  const retry = useCallback((msgId) => {
    const m = messages.find((x) => x.id === msgId);
    if (m?.retry && !busy) sendAi(m.retry.text, m.retry.cid, msgId);
  }, [busy, messages, sendAi]);

  const send = useCallback((raw) => {
    const text = raw.trim();
    if (!text) return;
    push({ role: "user", text });
    if (URGENT.test(text) && (flow || !status.ai_enabled)) push({ kind: "safety", text: SAFETY });
    if (flow) {
      const step = flow.steps[flow.i];
      advance(flow, step === "location" ? { location: text.slice(0, 160) } : { message: text.slice(0, 4000) });
    } else if (status.ai_enabled) {
      sendAi(text.slice(0, 800));
    }
  }, [advance, flow, push, sendAi, status.ai_enabled]);

  const reset = useCallback(async () => {
    setFlow(null);
    setMessages([]);
    await clearSession();
  }, []);

  const resolveReview = useCallback((msgId, result) => {
    setMessages((xs) => xs.map((m) => (m.id === msgId ? (result ? { id: m.id, kind: "result", ...result } : { id: m.id, kind: "cancelled" }) : m)));
  }, []);

  const inputMode = flow && flow.steps[flow.i] !== "services" ? "flow" : status.ai_enabled ? "ai" : "off";
  return { status, messages, busy, inputMode, action, send, retry, pickServices, startFlow, reset, resolveReview, flowPreset: flow?.data?.services };
}
